/**
 * AssetTagEditor - Component for editing scene and category tags on an uploading asset
 * Role/Motion is now handled in the card metadata line
 * Uses the same TAXONOMY_COLORS as the Library for consistency
 */

import { useState, useCallback } from "react";
import { X, Plus, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { AssetTags } from "./types";
import { TAXONOMY_COLORS } from "@/components/audio/audioTypes";
import type { SceneWithShots } from "@/types/shots";
import "./AssetTagEditor.css";

interface AssetTagEditorProps {
  tags: AssetTags;
  scenes: SceneWithShots[];
  onChange: (tags: AssetTags) => void;
}

export function AssetTagEditor({
  tags,
  scenes,
  onChange,
}: AssetTagEditorProps) {
  const [categoryInput, setCategoryInput] = useState("");

  // Handle scene change
  const handleSceneChange = useCallback(
    (sceneId: string | null) => {
      onChange({ ...tags, scene: sceneId });
    },
    [tags, onChange],
  );

  // Handle adding a category
  const handleAddCategory = useCallback(() => {
    const trimmed = categoryInput.trim();
    if (trimmed && !tags.category.includes(trimmed)) {
      onChange({
        ...tags,
        category: [...tags.category, trimmed],
      });
      setCategoryInput("");
    }
  }, [categoryInput, tags, onChange]);

  // Handle removing a category
  const handleRemoveCategory = useCallback(
    (category: string) => {
      onChange({
        ...tags,
        category: tags.category.filter((c) => c !== category),
      });
    },
    [tags, onChange],
  );

  // Handle category input key press
  const handleCategoryKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddCategory();
      }
    },
    [handleAddCategory],
  );

  // Get scene name by ID
  const getSceneName = (sceneId: string): string => {
    return scenes.find((s) => s.id === sceneId)?.name || "Scene";
  };

  return (
    <div className="asset-tag-editor">
      {/* Active tags as chips */}
      <div className="asset-tag-editor__tags">
        {/* Scene chip */}
        {tags.scene && (
          <span
            className="asset-tag-editor__tag"
            style={
              {
                "--tag-color": TAXONOMY_COLORS.scene.hex,
                "--tag-color-rgb": TAXONOMY_COLORS.scene.rgb,
              } as React.CSSProperties
            }
          >
            <span className="asset-tag-editor__tag-dot" />
            <span className="asset-tag-editor__tag-label">
              {getSceneName(tags.scene)}
            </span>
            <button
              type="button"
              onClick={() => handleSceneChange(null)}
              className="asset-tag-editor__tag-remove"
              aria-label={`Remove scene`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )}

        {/* Category chips */}
        {tags.category.map((category) => (
          <span
            key={category}
            className="asset-tag-editor__tag"
            style={
              {
                "--tag-color": TAXONOMY_COLORS.category.hex,
                "--tag-color-rgb": TAXONOMY_COLORS.category.rgb,
              } as React.CSSProperties
            }
          >
            <span className="asset-tag-editor__tag-dot" />
            <span className="asset-tag-editor__tag-label">{category}</span>
            <button
              type="button"
              onClick={() => handleRemoveCategory(category)}
              className="asset-tag-editor__tag-remove"
              aria-label={`Remove ${category}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Dropdowns for adding tags */}
      <div className="asset-tag-editor__dropdowns">
        {/* Scene dropdown */}
        {!tags.scene && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="asset-tag-editor__dropdown-trigger"
                style={
                  {
                    "--trigger-color": TAXONOMY_COLORS.scene.hex,
                    "--trigger-color-rgb": TAXONOMY_COLORS.scene.rgb,
                  } as React.CSSProperties
                }
              >
                <span className="asset-tag-editor__trigger-dot" />
                <span>Scene</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {scenes.length > 0 ? (
                scenes.map((scene) => (
                  <DropdownMenuItem
                    key={scene.id}
                    onClick={() => handleSceneChange(scene.id)}
                  >
                    {scene.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground">No scenes</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Category input */}
        <div
          className="asset-tag-editor__category-input-wrapper"
          style={
            {
              "--trigger-color": TAXONOMY_COLORS.category.hex,
              "--trigger-color-rgb": TAXONOMY_COLORS.category.rgb,
            } as React.CSSProperties
          }
        >
          <span className="asset-tag-editor__trigger-dot" />
          <input
            type="text"
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            onKeyDown={handleCategoryKeyDown}
            placeholder="Add tag..."
            className="asset-tag-editor__category-input"
          />
          {categoryInput.trim() && (
            <button
              type="button"
              onClick={handleAddCategory}
              className="asset-tag-editor__category-add"
              aria-label="Add category"
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
